import * as React from 'react'

import { TFAddonDetailBase } from '~graphql/generated/clientGraphql'
import { createContext } from '~logic/lib/react'

type TAddon = TFAddonDetailBase

type TControlContext = {
  closeAddonDetail: () => void
  openAddonDetail: (selectedAddon: TAddon) => void
}

type TAddonContext = {
  addon: Nullable<TAddon>
}

const [AddonProvider, useAddonDetailCtx] = createContext<TAddonContext>(`AddonDetail`)
const [ControlProvider, useAddonDetailControlCtx] =
  createContext<TControlContext>(`AddonDetailControl`)

type TProps = RequiredChildren

/**
 * Handles selecting a MAIN-type addon (from addon list on ProductDetail) for the addon detail modal.
 *  - Use the `useAddonDetail` hook to access the data needed by the AddonDetail (the selected addon).
 *  - Use the `useAddonDetailControl` hook to get functions for controlling the detail (modal/drawer), like open/close.
 */
export const AddonDetailProvider: React.FC<TProps> = ({ children }) => {
  const [addon, setAddon] = React.useState<TAddonContext['addon']>(null)

  const openAddonDetail = React.useCallback<TControlContext['openAddonDetail']>((selectedAddon) => {
    setAddon(selectedAddon)
  }, [])

  const closeAddonDetail = React.useCallback<TControlContext['closeAddonDetail']>(() => {
    setAddon(null)
  }, [])

  return (
    <ControlProvider value={{ openAddonDetail, closeAddonDetail }}>
      <AddonProvider value={{ addon }}>{children}</AddonProvider>
    </ControlProvider>
  )
}

export { useAddonDetailCtx, useAddonDetailControlCtx }
